import type { Brand } from "./types";

/**
 * 代客态品牌(1004 F1b)。
 *
 * 平台超管在主域代客渠道客户:`/public/brand` 只认访问域名 → 整页是直营品牌(直营人设名、更新记录、直营模块),
 * 平台人员看到的不是客户看到的。代客态改按**被代客租户所属渠道**套品牌:进入代客时用代客 token 拉
 * `GET /api/v1/me/brand`(形状同 /public/brand,只有展示字段)存本地;启动时(bootstrapBrand)发现代客中且有存档就套它;
 * 退出代客清掉,恢复按域名的品牌。
 *
 * 键与 ImpersonationContext 共用(meta 键在这里定义一份,context 从这里取,避免两处字面量漂移)。
 */
export const IMPERSONATION_META_KEY = "xbgeo-impersonation";
export const IMPERSONATION_BRAND_KEY = "xbgeo-impersonation-brand";
export const ME_BRAND_ENDPOINT = "/api/v1/me/brand";

function storage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

/** 代客中(meta 在且未过期)。 */
export function impersonationActive(s: Storage | null = storage()): boolean {
  if (!s) return false;
  try {
    const raw = s.getItem(IMPERSONATION_META_KEY);
    if (!raw) return false;
    const m = JSON.parse(raw) as { expiresAt?: number };
    return typeof m.expiresAt === "number" && m.expiresAt > Date.now();
  } catch {
    return false;
  }
}

/** 读代客品牌存档(代客中才返回;存的是已归一的 Brand,形状校验同 readBrandCache)。 */
export function readImpersonationBrand(s: Storage | null = storage()): Brand | null {
  if (!s || !impersonationActive(s)) return null;
  try {
    const raw = s.getItem(IMPERSONATION_BRAND_KEY);
    if (!raw) return null;
    const b = JSON.parse(raw) as Brand;
    if (!b || typeof b.name !== "string" || typeof b.key !== "string" || !b.modules) return null;
    return b;
  } catch {
    return null;
  }
}

export function clearImpersonationBrand(s: Storage | null = storage()): void {
  try {
    s?.removeItem(IMPERSONATION_BRAND_KEY);
  } catch {
    /* 忽略 */
  }
}

/**
 * 用代客 token 拉被代客租户的品牌并存档。失败(老后端没这个接口 / 超时 / 网络错)返回 false,不抛 ——
 * 品牌只是展示层,拉不到就沿用域名品牌,不能挡住进入代客。
 */
export async function fetchAndStoreImpersonationBrand(
  token: string,
  /** 回包 → Brand(传 bootstrap 的 normalizeBrandPayload;注入而不 import,免得 bootstrap ↔ 本文件成环)。 */
  normalize: (payload: unknown) => Brand | null,
  opts: { fetchImpl?: typeof fetch; timeoutMs?: number; storage?: Storage | null } = {}
): Promise<boolean> {
  const f = opts.fetchImpl ?? (typeof fetch !== "undefined" ? fetch : undefined);
  const s = opts.storage === undefined ? storage() : opts.storage;
  if (!f || !s) return false;
  const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = setTimeout(() => controller?.abort(), opts.timeoutMs ?? 3000);
  try {
    const res = await f(ME_BRAND_ENDPOINT, {
      method: "GET",
      headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      signal: controller?.signal,
    });
    if (!res.ok) return false;
    const brand = normalize((await res.json()) as unknown);
    if (!brand) return false;
    s.setItem(IMPERSONATION_BRAND_KEY, JSON.stringify(brand));
    return true;
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
